import React, { useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SmartImage } from './SmartImage';
import { packItems } from '../data/offer';
export function PackSlider() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const reduceMotion = useReducedMotion();
  const step = useCallback((): number => {
    const track = trackRef.current;
    if (!track) return 0;
    const first = track.firstElementChild as HTMLElement | null;
    if (!first) return 0;
    const gap = parseFloat(window.getComputedStyle(track).columnGap || '16') || 16;
    return first.offsetWidth + gap;
  }, []);
  const scrollByCard = (direction: -1 | 1) => {
    trackRef.current?.scrollBy({
      left: direction * step(),
      behavior: 'smooth'
    });
  };
  const goTo = (index: number) => {
    trackRef.current?.scrollTo({
      left: index * step(),
      behavior: 'smooth'
    });
  };
  const handleScroll = () => {
    const track = trackRef.current;
    const distance = step();
    if (!track || distance === 0) return;
    setActiveIndex(Math.min(packItems.length - 1, Math.round(track.scrollLeft / distance)));
  };
  return <div data-id="element-194" data-mp="3bk578bcvzxo9">
      <div className="flex items-center justify-between gap-4" data-id="element-195" data-mp="3bjlr906srjcv">
        <p className="text-[13px] font-medium text-moss-600" data-id="element-196" data-mp="2wne5vwjnkbw0">
          Deslizá y tocá “Ver qué incluye” en cada recurso
        </p>
        <div className="hidden gap-2 lg:flex" data-id="element-197" data-mp="31lfqilwq8j9l">
          <button type="button" onClick={() => scrollByCard(-1)} aria-label="Ver recurso anterior" className="flex h-10 w-10 items-center justify-center rounded-full border border-cream-300 bg-cream-50 text-forest-800 transition-colors duration-150 ease-out hover:border-forest-800 hover:bg-forest-800 hover:text-cream-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-800" data-id="element-198" data-mp="2noarv6zcemod">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-199" data-mp="kepg8ssh4bso" />
          </button>
          <button type="button" onClick={() => scrollByCard(1)} aria-label="Ver recurso siguiente" className="flex h-10 w-10 items-center justify-center rounded-full border border-cream-300 bg-cream-50 text-forest-800 transition-colors duration-150 ease-out hover:border-forest-800 hover:bg-forest-800 hover:text-cream-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-800" data-id="element-200" data-mp="ffw2cz99yirz">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-201" data-mp="tfx6cnku66bo" />
          </button>
        </div>
      </div>

      <ul ref={trackRef} onScroll={handleScroll} className="-mx-5 mt-4 flex snap-x snap-mandatory items-start gap-4 overflow-x-auto px-5 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0" data-id="element-202" data-mp="1qvt45o27ox6m">
        {packItems.map((item, index) => {
        const isOpen = openId === item.id;
        const isMain = item.kind === 'principal';
        return <li key={item.id} className={`flex w-[80%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border transition-colors duration-150 ease-out sm:w-[46%] lg:w-[calc((100%-2rem)/3)] ${isMain ? 'border-forest-800 bg-forest-900 text-cream-100' : 'border-cream-300 bg-cream-100 text-forest-900'}`} data-id="element-203" data-mp="23ddknk9mosa7" data-mpi={index}>
              <div className="relative" data-id="element-204" data-mp="2m5pn070a7tkc">
                <SmartImage src={item.image} alt={item.imageAlt} width={1200} height={800} sizes="(min-width: 1024px) 340px, (min-width: 640px) 46vw, 80vw" priority={index === 0} wrapperClassName="h-24 w-full sm:h-28" className="h-full w-full object-cover" data-id="element-205" data-mp="2m6r0sj8omu5r" />
                <span className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-display text-[10px] font-bold uppercase tracking-[0.14em] ${isMain ? 'bg-forest-900 text-moss-400' : 'bg-cream-50 text-moss-600'}`} data-id="element-206" data-mp="3su7i1j6rh9nd">
                  <span aria-hidden="true" className="text-xs leading-none" data-id="element-207" data-mp="3ev7vch2t74tu">
                    {item.emoji}
                  </span>
                  {item.label}
                </span>
              </div>

              <div className="flex min-h-[124px] flex-col px-4 pt-3" data-id="element-208" data-mp="3nvnwvkzzs1w2">
                <h3 className={`font-display text-[15px] font-extrabold leading-snug sm:text-base ${isMain ? 'text-cream-50' : 'text-forest-900'}`} data-id="element-209" data-mp="3iv9x0q8jny94">
                  {item.title}
                </h3>
                <p className={`mt-1.5 line-clamp-2 text-[13px] leading-snug ${isMain ? 'text-moss-200' : 'text-forest-800/80'}`} data-id="element-210" data-mp="16mkw2qbx0bqh">
                  {item.teaser}
                </p>

                <p className="mt-auto flex items-baseline gap-2 pt-3 text-[13px]" data-id="element-211" data-mp="wpyc03rexopi">
                  <span className={isMain ? 'font-display text-base font-bold text-cream-50' : 'text-forest-800/60 line-through'} data-id="element-212" data-mp="11ojbg0yuc4cw">
                    {item.price}
                  </span>
                  <span className={`font-display text-[10px] font-bold uppercase tracking-wider ${isMain ? 'text-moss-400' : 'text-moss-600'}`} data-id="element-213" data-mp="1fmq1iuxjt020">
                    {item.priceNote}
                  </span>
                </p>
              </div>

              <button type="button" onClick={() => setOpenId(isOpen ? null : item.id)} aria-expanded={isOpen} aria-controls={`pack-panel-${item.id}`} className={`mx-4 mb-4 mt-3 flex items-center justify-between gap-3 rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wide transition-[background-color,transform] duration-150 ease-out active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${isMain ? 'bg-cream-50 text-forest-900 hover:bg-cream-200 focus-visible:outline-cream-50' : 'bg-forest-800 text-cream-50 hover:bg-forest-950 focus-visible:outline-forest-800'}`} data-id="element-214" data-mp="2d4ggqzqzl0wa">
                {isOpen ? 'Ocultar detalle' : 'Ver qué incluye'}
                <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} data-id="element-215" data-mp="1l8qjhs376uqr" />
              </button>

              <AnimatePresence initial={false} data-id="element-216" data-mp="q6dgkryfdnl">
                {isOpen && <motion.div id={`pack-panel-${item.id}`} initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: reduceMotion ? 0 : 0.24,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-217" data-mp="4sb55dyue07l">
                    <div className={`mx-4 mb-4 border-t pt-3 ${isMain ? 'border-forest-700' : 'border-cream-300'}`} data-id="element-218" data-mp="3i7h9afrvcmsa">
                      <p className={`text-sm font-semibold leading-snug ${isMain ? 'text-cream-50' : 'text-forest-900'}`} data-id="element-219" data-mp="2pi0exckouvtf">
                        {item.headline}
                      </p>
                      <div className={`mt-2 space-y-2.5 text-sm leading-relaxed ${isMain ? 'text-moss-200' : 'text-forest-800/85'}`} data-id="element-220" data-mp="1iwivunnnhl6r">
                        {item.paragraphs.map(paragraph => <p key={paragraph} data-id="element-221" data-mp="26swr737abai1">{paragraph}</p>)}
                      </div>
                      <p className={`mt-4 font-display text-[11px] font-bold uppercase tracking-[0.14em] ${isMain ? 'text-moss-400' : 'text-forest-900'}`} data-id="element-222" data-mp="21tsj6fw3jita">
                        {item.listTitle}
                      </p>
                      <ul className="mt-2 space-y-1.5" data-id="element-223" data-mp="2pkfrv3du6cgw">
                        {item.items.map(listItem => <li key={listItem} className={`flex gap-2.5 text-sm ${isMain ? 'text-moss-200' : 'text-forest-800'}`} data-id="element-224" data-mp="2kj8l97nfjjbt">
                            <CheckIcon aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${isMain ? 'text-moss-400' : 'text-moss-600'}`} data-id="element-225" data-mp="2v5ncfq6f2u06" />
                            <span data-id="element-226" data-mp="2q8ol1fe5115o">{listItem}</span>
                          </li>)}
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </li>;
      })}
      </ul>

      <div className="mt-3 flex items-center gap-2" role="tablist" aria-label="Recursos incluidos" data-id="element-227" data-mp="22ikk5fsc3fid">
        {packItems.map((item, index) => <button key={item.id} type="button" role="tab" aria-selected={activeIndex === index} aria-label={`Ir a ${item.title}`} onClick={() => goTo(index)} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-800 ${activeIndex === index ? 'w-7 bg-forest-800' : 'w-1.5 bg-cream-300'}`} data-id="element-228" data-mp="27if9hewgnx1b" data-mpi={index} />)}
      </div>
    </div>;
}